import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getHireCode,
  reissuePersonalHireCode,
  revokeHireCode,
  type HireCodeDetail,
} from "@admin/lib/api";
import { formatDate } from "@admin/lib/utils";
import { Button } from "@admin/components/ui/button";
import { Input } from "@admin/components/ui/input";
import { Label } from "@admin/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";

export default function HireCodeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<HireCodeDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [userId, setUserId] = useState("");

  useEffect(() => {
    if (!id) return;
    getHireCode(id)
      .then(setItem)
      .catch(() => setErr("Invite code not found"));
  }, [id]);

  const onRevoke = async () => {
    if (!id) return;
    setBusy(true);
    setErr(null);
    try {
      await revokeHireCode(id);
      const updated = await getHireCode(id);
      setItem(updated);
    } catch {
      setErr("Could not revoke code");
    } finally {
      setBusy(false);
    }
  };

  const onReissue = async () => {
    if (!userId.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await reissuePersonalHireCode(userId.trim());
      setUserId("");
    } catch {
      setErr("Could not reissue personal code");
    } finally {
      setBusy(false);
    }
  };

  if (err && !item) {
    return (
      <div className="space-y-2">
        <p className="text-danger">{err}</p>
        <Link to="/hire/codes" className="text-sm text-accent hover:underline">
          Back to codes
        </Link>
      </div>
    );
  }
  if (!item) return <p className="text-muted-fg">Loading...</p>;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <Link to="/hire/codes" className="text-sm text-accent hover:underline">
          Hire codes
        </Link>
        <h1 className="mt-2 font-mono text-2xl font-semibold">{item.code}</h1>
        <p className="text-sm text-muted-fg">{item.sharePath}</p>
      </div>
      {err && <p className="text-danger">{err}</p>}

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 text-sm md:grid-cols-2">
          <div>Status: {item.status}</div>
          <div>Type: {item.codeType}</div>
          <div>Redemptions: {item.redemptionCount}</div>
          <div>Campaign: {item.campaignTag ?? "-"}</div>
          <div>Personal referral: {item.isPersonalReferral ? "yes" : "no"}</div>
          <div>Created: {formatDate(item.createdAt)}</div>
          <div className="md:col-span-2">Note: {item.note ?? "-"}</div>
        </CardContent>
      </Card>

      {item.status === "active" ? (
        <Button variant="outline" disabled={busy} onClick={() => void onRevoke()}>
          Revoke code
        </Button>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Reissue personal code</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-2">
            <Label htmlFor="user-id">User ID</Label>
            <Input id="user-id" value={userId} onChange={(e) => setUserId(e.target.value)} className="min-w-80" />
          </div>
          <Button disabled={busy || !userId.trim()} onClick={() => void onReissue()}>
            Reissue
          </Button>
        </CardContent>
      </Card>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-muted/50 text-muted-fg">
            <tr>
              <th className="px-4 py-3 font-medium">User</th>
              <th className="px-4 py-3 font-medium">Redeemed</th>
            </tr>
          </thead>
          <tbody>
            {item.redemptions.map((r) => (
              <tr key={r.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3">
                  <Link to={`/users/${r.userId}`} className="text-accent hover:underline">
                    {r.userEmail}
                  </Link>
                </td>
                <td className="px-4 py-3 text-muted-fg">{formatDate(r.redeemedAt)}</td>
              </tr>
            ))}
            {item.redemptions.length === 0 && (
              <tr>
                <td colSpan={2} className="px-4 py-8 text-center text-muted-fg">
                  No redemptions yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
